import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../AuthContext';
import { formatDateOnly, normalizeDateInput } from '../utils/dateOnly';
import { normalizeStoredUrl } from '../utils/assets';
import useAuthenticatedAssetUrl from '../hooks/useAuthenticatedAssetUrl';

import { MIN_PASSWORD_LENGTH } from '../utils/passwordPolicy';

const formatCpf = (cpf) => {
  if (!cpf) return '—';
  const digits = String(cpf).replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export default function Profile({
  forcePasswordChange = false,
  closeTab,
  activateTab,
}) {
  const { user, updateUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthDate, setBirthDate] = useState(normalizeDateInput(user?.birthDate));
  const [notifyByEmail, setNotifyByEmail] = useState(
    user?.emailNotificationsEnabled !== false,
  );
  const [photoPreview, setPhotoPreview] = useState(() =>
    normalizeStoredUrl(user?.photoUrl || ''),
  );
  const [photoFile, setPhotoFile] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const photoInputRef = useRef(null);
  const currentPasswordInputRef = useRef(null);

  useEffect(() => {
    setName(user?.name || '');
    setBirthDate(normalizeDateInput(user?.birthDate));
    setNotifyByEmail(user?.emailNotificationsEnabled !== false);
    setCurrentPassword('');
    setPassword('');
    setConfirmPassword('');
    setPhotoPreview(normalizeStoredUrl(user?.photoUrl || ''));
    setPhotoFile(null);
    setPhotoRemoved(false);
  }, [user]);

  const mustChangePassword = Boolean(user?.mustChangePassword);
  const requirePasswordChange = forcePasswordChange || mustChangePassword;

  useEffect(() => {
    if (!requirePasswordChange) return undefined;
    const focusTimer = window.setTimeout(() => {
      currentPasswordInputRef.current?.focus();
      currentPasswordInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
    return () => window.clearTimeout(focusTimer);
  }, [requirePasswordChange]);

  const hasChanges = useMemo(() => {
    const originalName = user?.name || '';
    const originalBirthDate = normalizeDateInput(user?.birthDate);
    const originalNotify = user?.emailNotificationsEnabled !== false;
    const originalPhoto = normalizeStoredUrl(user?.photoUrl || '');
    const previewPhoto = normalizeStoredUrl(photoPreview || '');
    const hasPhotoChanges =
      !requirePasswordChange &&
      (photoRemoved || Boolean(photoFile) || previewPhoto !== originalPhoto);

    return (
      Boolean(password) ||
      name.trim() !== originalName ||
      birthDate !== originalBirthDate ||
      notifyByEmail !== originalNotify ||
      hasPhotoChanges
    );
  }, [
    birthDate,
    name,
    notifyByEmail,
    password,
    photoFile,
    photoPreview,
    photoRemoved,
    requirePasswordChange,
    user,
  ]);

  const profileInfo = [
    { label: 'Nome', value: user?.name || '—', colSpan: 'md:col-span-2' },
    {
      label: 'E-mail',
      value: user?.email || '—',
      colSpan: 'md:col-span-2',
    },
    { label: 'Grupo', value: user?.group?.name || '—' },
    {
      label: 'Data de nascimento',
      value: formatDateOnly(normalizeDateInput(user?.birthDate)),
    },
    { label: 'Setor', value: user?.sector?.name || '—' },
    { label: 'CPF', value: formatCpf(user?.cpf) },
    {
      label: 'Notificações por e-mail',
      value: notifyByEmail ? 'Ativadas' : 'Desativadas',
      colSpan: 'md:col-span-2',
    },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hasChanges) return;

    const trimmedName = name.trim();
    const passwordProvided = password.length > 0;

    if (!trimmedName) {
      window.alert('Informe um nome para continuar.');
      return;
    }

    if (requirePasswordChange && !passwordProvided) {
      window.alert('Informe uma nova senha para continuar.');
      return;
    }

    if (passwordProvided && !currentPassword) {
      window.alert('Informe sua senha atual para definir uma nova senha.');
      return;
    }

    if (passwordProvided && password.length < MIN_PASSWORD_LENGTH) {
      window.alert(
        `A nova senha deve conter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`,
      );
      return;
    }

    if (passwordProvided && password !== confirmPassword) {
      window.alert('As senhas digitadas não coincidem.');
      return;
    }

    setLoading(true);

    try {
      let nextPhotoUrl = user?.photoUrl ?? null;
      let photoHasChanged = false;

      if (!requirePasswordChange) {
        if (photoRemoved) {
          nextPhotoUrl = null;
          photoHasChanged = true;
        }
        if (photoFile) {
          const formData = new FormData();
          formData.append('file', photoFile);
          const uploadResponse = await api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const uploadData = uploadResponse?.data ?? {};
          nextPhotoUrl =
            uploadData.downloadPath ||
            uploadData.downloadUrl ||
            uploadData.url ||
            null;
          photoHasChanged = true;
        }
      }

      const payload = {
        name: trimmedName,
        birthDate: birthDate || null,
        emailNotificationsEnabled: notifyByEmail,
        ...(photoHasChanged ? { photoUrl: nextPhotoUrl } : {}),
        ...(passwordProvided
          ? {
              currentPassword,
              password,
            }
          : {}),
      };

      const response = await api.put('/users/me', payload);

      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');

      if (passwordProvided) {
        window.alert(
          'Senha alterada com sucesso. Por segurança, entre novamente em todos os dispositivos.',
        );
        await logout();
        return;
      }

      updateUser(response.data);
      setName(response.data.name || '');
      setBirthDate(normalizeDateInput(response.data.birthDate));
      setNotifyByEmail(response.data.emailNotificationsEnabled !== false);
      setPhotoPreview(normalizeStoredUrl(response.data.photoUrl || ''));
      setPhotoFile(null);
      setPhotoRemoved(false);
      window.alert('Perfil atualizado');

      if (requirePasswordChange) {
        activateTab?.('home');
        closeTab?.({ activateTabId: 'home' });
      }
    } catch (error) {
      const message =
        error.response?.data?.error || 'Erro ao atualizar o perfil.';
      window.alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (event) => {
    if (requirePasswordChange) return;
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      window.alert('Selecione um arquivo de imagem.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);
    setPhotoFile(file);
    setPhotoRemoved(false);
    if (event.target) event.target.value = '';
  };

  const handlePhotoRemove = () => {
    if (requirePasswordChange) return;
    setPhotoPreview('');
    setPhotoFile(null);
    setPhotoRemoved(true);
  };

  const authenticatedPhotoPreviewUrl = useAuthenticatedAssetUrl(
    photoFile ? '' : photoPreview,
    {
      path:
        !photoFile && !photoRemoved && user?.id && photoPreview
          ? `/users/${user.id}/photo`
          : '',
    },
  );
  const resolvedPhotoPreviewUrl =
    photoFile || /^(data:|blob:)/i.test(photoPreview)
      ? photoPreview
      : authenticatedPhotoPreviewUrl;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {requirePasswordChange && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-700 shadow">
          <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-amber-200 text-amber-600">
            <FaExclamationTriangle />
          </span>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide">
              Troca de senha obrigatória
            </h2>
            <p className="text-sm text-amber-700">
              Por segurança, informe a senha atual e defina uma nova senha antes
              de continuar utilizando a plataforma.
            </p>
          </div>
        </div>
      )}

      <p className="text-gray-600">
        Consulte seus dados, ajuste preferências e altere sua senha.
      </p>

      <section className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-medium mb-3">Informações pessoais</h2>
        <div className="space-y-1 mb-4">
          <p className="font-medium">{user?.name || 'Usuário'}</p>
          <p className="text-sm text-gray-500 break-words">
            {user?.email || '—'}
          </p>
          <p className="text-sm text-gray-500">{user?.group?.name}</p>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profileInfo.map((item) => (
            <div key={item.label} className={`space-y-1 ${item.colSpan || ''}`}>
              <dt className="text-sm text-gray-500">{item.label}</dt>
              <dd className="text-base font-medium text-gray-900">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 text-xs text-gray-500">
          E-mail, CPF, grupo e setor são dados corporativos e devem ser
          atualizados por um administrador.
        </p>
      </section>

      <section className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-medium mb-3">Atualizar perfil e acesso</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            <span className="mb-1 block">Nome completo</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="border rounded px-2 py-1 w-full"
              placeholder="Como deseja ser identificado"
              required
            />
          </label>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Foto de perfil
            </span>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {resolvedPhotoPreviewUrl ? (
                <img
                  src={resolvedPhotoPreviewUrl}
                  alt="Pré-visualização da foto do perfil"
                  className="h-20 w-20 rounded-full object-cover border"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-gray-300 text-xs text-gray-500">
                  Sem foto
                </div>
              )}
              <div className="flex flex-col gap-2 text-sm font-medium">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={requirePasswordChange}
                  className="rounded border border-blue-200 px-3 py-1 text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Escolher foto
                </button>
                {photoPreview && (
                  <button
                    type="button"
                    onClick={handlePhotoRemove}
                    disabled={requirePasswordChange}
                    className="rounded border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Remover foto
                  </button>
                )}
                <p className="text-xs font-normal text-gray-500">
                  {requirePasswordChange
                    ? 'Conclua primeiro a troca obrigatória de senha.'
                    : 'Utilize imagens JPG, PNG, GIF ou WebP.'}
                </p>
              </div>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handlePhotoChange}
              disabled={requirePasswordChange}
              className="hidden"
            />
          </div>

          <label className="block text-sm font-medium text-gray-700">
            <span className="mb-1 block">Data de nascimento</span>
            <input
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              className="border rounded px-2 py-1 w-full"
            />
          </label>

          <div>
            <label
              className="block mb-1 font-medium"
              htmlFor="current-password"
            >
              Senha atual
              {requirePasswordChange && (
                <span className="ml-1 text-xs font-semibold text-amber-600">
                  (obrigatória)
                </span>
              )}
            </label>
            <div className="relative">
              <input
                id="current-password"
                ref={currentPasswordInputRef}
                name="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                className="w-full rounded border px-2 py-1 pr-10"
                required={requirePasswordChange || Boolean(password)}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((previous) => !previous)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r text-gray-500 transition hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                aria-label={
                  showCurrentPassword
                    ? 'Ocultar senha atual'
                    : 'Mostrar senha atual'
                }
                aria-pressed={showCurrentPassword}
              >
                {showCurrentPassword ? (
                  <FiEyeOff aria-hidden />
                ) : (
                  <FiEye aria-hidden />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium" htmlFor="new-password">
              Nova senha
              {requirePasswordChange && (
                <span className="ml-1 text-xs font-semibold text-amber-600">
                  (obrigatória)
                </span>
              )}
            </label>
            <div className="relative">
              <input
                id="new-password"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                className="w-full rounded border px-2 py-1 pr-10"
                required={requirePasswordChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword((previous) => !previous)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r text-gray-500 transition hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                aria-label={showPassword ? 'Ocultar nova senha' : 'Mostrar nova senha'}
                aria-pressed={showPassword}
              >
                {showPassword ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Use pelo menos {MIN_PASSWORD_LENGTH} caracteres.
            </p>
          </div>

          <div>
            <label
              className="block mb-1 font-medium"
              htmlFor="confirm-password"
            >
              Confirmar nova senha
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                className="w-full rounded border px-2 py-1 pr-10"
                required={Boolean(password) || requirePasswordChange}
              />
              <button
                type="button"
                onClick={() =>
                  setShowConfirmPassword((previous) => !previous)
                }
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r text-gray-500 transition hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                aria-label={
                  showConfirmPassword
                    ? 'Ocultar confirmação de senha'
                    : 'Mostrar confirmação de senha'
                }
                aria-pressed={showConfirmPassword}
              >
                {showConfirmPassword ? (
                  <FiEyeOff aria-hidden />
                ) : (
                  <FiEye aria-hidden />
                )}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={notifyByEmail}
                onChange={(event) => setNotifyByEmail(event.target.checked)}
                className="mt-1"
              />
              <span>
                Receber notificações por e-mail quando novas tarefas forem
                atribuídas a você. O envio utiliza o e-mail corporativo do seu
                cadastro.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !hasChanges}
            className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            {loading ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </form>
      </section>
    </div>
  );
}
